import { zodResolver } from '@hookform/resolvers/zod';
import { domainSchema, emailSchema, slugSchema } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Globe, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, Input, Skeleton } from '@/components/ui/primitives';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import type { PlatformTenant } from '@/lib/types';
import { formatDate } from '@/lib/utils';

const createSchema = z.object({
  name: z.string().trim().min(2, 'Mindestens 2 Zeichen').max(120),
  slug: slugSchema,
  domain: z.union([domainSchema, z.literal('')]),
  adminFirstName: z.string().trim().min(1, 'Pflichtfeld'),
  adminLastName: z.string().trim().min(1, 'Pflichtfeld'),
  adminEmail: emailSchema,
});
type CreateForm = z.infer<typeof createSchema>;

function CreateTenantDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const form = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', slug: '', domain: '', adminFirstName: '', adminLastName: '', adminEmail: '' },
  });
  const mutation = useMutation({
    mutationFn: (v: CreateForm) =>
      api('/platform/tenants', {
        method: 'POST',
        body: {
          name: v.name,
          slug: v.slug,
          domains: v.domain ? [v.domain] : [],
          admin: { email: v.adminEmail, firstName: v.adminFirstName, lastName: v.adminLastName },
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      toast.success('Mandant angelegt – der Administrator wurde eingeladen.');
      form.reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const err = form.formState.errors;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate className="flex min-h-0 flex-col">
          <DialogHeader>
            <DialogTitle>Neuer Mandant</DialogTitle>
            <DialogDescription>Standardrollen und Nummernkreise werden automatisch angelegt.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <Field label="Firmenname" htmlFor="n-name" error={err.name?.message}>
              <Input
                id="n-name"
                {...form.register('name', {
                  onChange: (e) => {
                    if (!form.formState.dirtyFields.slug) {
                      const slug = String(e.target.value)
                        .toLowerCase()
                        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-|-$/g, '')
                        .slice(0, 48);
                      form.setValue('slug', slug);
                    }
                  },
                })}
              />
            </Field>
            <Field label="Kürzel" htmlFor="n-slug" error={err.slug?.message} hint="Eindeutig, z. B. für Links ohne eigene Domain">
              <Input id="n-slug" className="font-mono" {...form.register('slug')} />
            </Field>
            <Field label="Domain (optional)" htmlFor="n-domain" error={err.domain?.message} hint="z. B. service.kunde.de – DNS muss auf diese Installation zeigen">
              <Input id="n-domain" className="font-mono" {...form.register('domain')} />
            </Field>
            <p className="pt-2 text-sm font-medium">Erster Administrator</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Vorname" htmlFor="n-first" error={err.adminFirstName?.message}>
                <Input id="n-first" {...form.register('adminFirstName')} />
              </Field>
              <Field label="Nachname" htmlFor="n-last" error={err.adminLastName?.message}>
                <Input id="n-last" {...form.register('adminLastName')} />
              </Field>
            </div>
            <Field label="E-Mail-Adresse" htmlFor="n-email" error={err.adminEmail?.message}>
              <Input id="n-email" type="email" {...form.register('adminEmail')} />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Mandant anlegen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TenantDetailDialog({ tenant, onOpenChange }: { tenant: PlatformTenant | null; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [domain, setDomain] = useState('');
  const [confirmDialog, confirm] = useConfirm();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
  const onError = (e: unknown) => toast.error(errorMessage(e));

  const addDomain = useMutation({
    mutationFn: () => api(`/platform/tenants/${tenant!.id}/domains`, { method: 'POST', body: { domain } }),
    onSuccess: () => {
      setDomain('');
      invalidate();
    },
    onError,
  });
  const domainAction = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'primary' | 'delete' }) =>
      action === 'primary'
        ? api(`/platform/tenants/${tenant!.id}/domains/${id}/primary`, { method: 'POST' })
        : api(`/platform/tenants/${tenant!.id}/domains/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError,
  });
  const setStatus = useMutation({
    mutationFn: (status: 'ACTIVE' | 'SUSPENDED') => api(`/platform/tenants/${tenant!.id}`, { method: 'PATCH', body: { status } }),
    onSuccess: (_d, status) => {
      invalidate();
      toast.success(status === 'SUSPENDED' ? 'Mandant gesperrt' : 'Mandant aktiviert');
    },
    onError,
  });
  const domainValid = domainSchema.safeParse(domain).success;

  return (
    <Dialog open={tenant !== null} onOpenChange={onOpenChange}>
      {confirmDialog}
      <DialogContent>
        {tenant && (
          <>
            <DialogHeader>
              <DialogTitle>{tenant.name}</DialogTitle>
              <DialogDescription>
                Kürzel <span className="font-mono">{tenant.slug}</span> · {tenant.userCount} Benutzer · angelegt {formatDate(tenant.createdAt)}
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="flex flex-col gap-6">
              <section>
                <h3 className="mb-2 text-sm font-semibold">Domains</h3>
                <ul className="mb-3 flex flex-col divide-y rounded-md border">
                  {tenant.domains.length === 0 && <li className="p-3 text-sm text-muted-foreground">Keine Domain – Zugriff nur über das Kürzel.</li>}
                  {tenant.domains.map((d) => (
                    <li key={d.id} className="flex items-center gap-2 p-2.5 text-sm">
                      <Globe className="size-4 text-muted-foreground" />
                      <span className="flex-1 truncate font-mono">{d.domain}</span>
                      {d.isPrimary ? (
                        <Badge>primär</Badge>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => domainAction.mutate({ id: d.id, action: 'primary' })} title="Als primär festlegen">
                          <Star />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        aria-label={`${d.domain} entfernen`}
                        onClick={async () => {
                          if (await confirm({ title: `Domain ${d.domain} entfernen?`, confirmLabel: 'Entfernen', destructive: true })) {
                            domainAction.mutate({ id: d.id, action: 'delete' });
                          }
                        }}
                      >
                        <Trash2 />
                      </Button>
                    </li>
                  ))}
                </ul>
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (domainValid) addDomain.mutate();
                  }}
                >
                  <Input placeholder="service.kunde.de" className="font-mono" value={domain} onChange={(e) => setDomain(e.target.value.trim().toLowerCase())} aria-label="Neue Domain" />
                  <Button type="submit" variant="outline" disabled={!domainValid} loading={addDomain.isPending}>
                    Hinzufügen
                  </Button>
                </form>
              </section>
              {!tenant.isPlatform && (
                <section className="rounded-lg border border-destructive/30 p-4">
                  <h3 className="text-sm font-semibold">Status</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {tenant.status === 'ACTIVE'
                      ? 'Ein gesperrter Mandant kann sich nicht mehr anmelden; alle Sitzungen werden beendet. Daten bleiben erhalten.'
                      : 'Der Mandant ist gesperrt.'}
                  </p>
                  <Button
                    className="mt-3"
                    size="sm"
                    variant={tenant.status === 'ACTIVE' ? 'destructive' : 'default'}
                    loading={setStatus.isPending}
                    onClick={async () => {
                      if (tenant.status === 'ACTIVE') {
                        const ok = await confirm({ title: `${tenant.name} sperren?`, confirmLabel: 'Sperren', destructive: true });
                        if (ok) setStatus.mutate('SUSPENDED');
                      } else setStatus.mutate('ACTIVE');
                    }}
                  >
                    {tenant.status === 'ACTIVE' ? 'Mandant sperren' : 'Mandant aktivieren'}
                  </Button>
                </section>
              )}
            </DialogBody>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TenantsPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['platform', 'tenants'], queryFn: () => api<PlatformTenant[]>('/platform/tenants') });
  const selected = data?.find((t) => t.id === selectedId) ?? null;

  return (
    <>
      <PageHeader
        title="Mandanten"
        description="Unternehmen, die diese Installation nutzen"
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus /> Neuer Mandant
          </Button>
        }
      />
      <Card>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Mandant</TH>
              <TH className="hidden md:table-cell">Domains</TH>
              <TH>Status</TH>
              <TH className="hidden sm:table-cell">Benutzer</TH>
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={4}>
                  <Skeleton className="h-24" />
                </TD>
              </TR>
            )}
            {data?.length === 0 && <EmptyRow colSpan={4}>Noch keine Mandanten.</EmptyRow>}
            {data?.map((t) => (
              <TR key={t.id} className="cursor-pointer" onClick={() => setSelectedId(t.id)}>
                <TD>
                  <div className="flex items-center gap-3">
                    <Building2 className="size-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">
                        {t.name} {t.isPlatform && <Badge className="ml-1">Plattform</Badge>}
                      </p>
                      <p className="font-mono text-xs text-muted-foreground">{t.slug}</p>
                    </div>
                  </div>
                </TD>
                <TD className="hidden font-mono text-xs md:table-cell">{t.domains.map((d) => d.domain).join(', ') || '–'}</TD>
                <TD>{t.status === 'ACTIVE' ? <Badge variant="success">Aktiv</Badge> : <Badge variant="destructive">Gesperrt</Badge>}</TD>
                <TD className="hidden tabular-nums sm:table-cell">{t.userCount}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>
      <CreateTenantDialog open={createOpen} onOpenChange={setCreateOpen} />
      <TenantDetailDialog tenant={selected} onOpenChange={(o) => !o && setSelectedId(null)} />
    </>
  );
}
